import { createSelector } from "reselect";

import {
    IBrand,
    IRetailMatchChartData,
    IRetailMatchJob,
    IRetailMatchJobResult,
    ISegment,
    IStoreScoreRecord,
} from "../../Models/Models";
import { IRootReducer } from "../../Models/IRootReducer";
import { formatLongAddressFromRecord } from "../../Components/Apps/RetailMatch/Logic/SharedMethods";
import { getBenchmarkAddressOverride } from "../../Util/RetailMatch/RetailMatchUtil";
import { IBuxtonDeck } from "../../Models/IBuxtonDeck";
import { IRetailMatchGeography } from "../../Models/IRetailMatchGeography";
import { ActiveApp } from "../../Enums/ActiveApp";

export const getRetailMatchActiveApp = (state: IRootReducer): ActiveApp =>
    state.loadScout.activeApp;

export const getRetailMatchResult = (
    state: IRootReducer
): IRetailMatchJobResult => state.matchReportOutput.jobResult;

export const getSelectedBrand = (state: IRootReducer): IBrand =>
    state.matchReportOutput.selectedBrand;

export const getRetailBenchmarkMatchResult = (state: IRootReducer): IStoreScoreRecord =>
    state.matchReportOutput.benchmarkRecord;

export const getRetailMatchJobChartData = (
    state: IRootReducer
): IRetailMatchChartData => state.matchReportOutput.jobResult.chartData;

const getRetailMatchOverviewMap = (state: IRootReducer): google.maps.Map =>
    state.reportMap.retailMatchOverviewMap;

const getRetailMatchOverviewMapMarkers = (
    state: IRootReducer
): google.maps.Marker[] => state.reportMap.retailMatchOverviewMapMarkers;

const getRetailMatchLocationsAnalyzedMap = (
    state: IRootReducer
): google.maps.Map => state.reportMap.googleMap;

const getRetailMatchLocationsAnalyzedMapMarkers = (
    state: IRootReducer
): google.maps.Marker[] => state.reportMap.currentMarkers;

const getRetailMatchLocationsAnalyzedMapCenter = (
    state: IRootReducer
): google.maps.LatLng => state.reportMap.mapCenter;

const getBuxtonDeck = (state: IRootReducer): IBuxtonDeck =>
    state.reportMap.buxtonDeck;

const getRetailMatchGeographies = (
    state: IRootReducer
): IRetailMatchGeography[] => state.matchReportOutput.geographies;

export const retailMatchActiveAppSelector = createSelector(
    [getRetailMatchActiveApp],
    (activeApp) => activeApp
);

export const retailMatchLocationsAnalyzedMapDataSelector = createSelector(
    [
        getRetailMatchLocationsAnalyzedMap,
        getRetailMatchLocationsAnalyzedMapMarkers,
        getRetailMatchLocationsAnalyzedMapCenter,
        getBuxtonDeck,
        getRetailMatchGeographies,
    ],
    (
        googleMap,
        currentMapMarkers,
        mapCenter,
        buxtonDeck,
        geographies
    ): {
    googleMap: google.maps.Map;
    currentMapMarkers: google.maps.Marker[];
    mapCenter: google.maps.LatLng;
    buxtonDeck: IBuxtonDeck;
    geographies: IRetailMatchGeography[];
  } => ({
        googleMap,
        currentMapMarkers,
        mapCenter,
        buxtonDeck,
        geographies,
    })
);

export const retailMatchMultiBrandDataSelector = createSelector(
    [
        getRetailMatchOverviewMap,
        getRetailMatchOverviewMapMarkers,
        getSelectedBrand,
    ],
    (
        map,
        markers,
        brand
    ): {
    map: google.maps.Map;
    markers: google.maps.Marker[];
    brand: IBrand;
  } => ({
        map,
        markers,
        brand,
    })
);

export const retailMatchJobOverviewSelector = createSelector(
    [getRetailMatchJobChartData],
    (chartData: IRetailMatchChartData) => chartData.overview
);

export const retailMatchJobSelector = createSelector(
    (state: IRootReducer) => state.matchReportOutput.jobResult.job,
    (job: IRetailMatchJob) => job
);

export const retailMatchJobResultSelector = createSelector(
    getRetailMatchResult,
    (result: IRetailMatchJobResult) => result
);

export const retailMatchBenchmarkJobResultSelector = createSelector(
    getRetailBenchmarkMatchResult,
    (benchmarkRecord: IStoreScoreRecord) => benchmarkRecord
);

export const retailMatchLoadingUpdateJobSelector = createSelector(
    (state: IRootReducer) => state.matchReportOutput.loadingUpdateJob,
    (loadingUpdateJob: boolean) => loadingUpdateJob
);

export const retailMatchReportDataSelector = createSelector(
    [getRetailMatchResult, getRetailBenchmarkMatchResult, getSelectedBrand],
    (jobResult, benchmarkRecord, selectedBrand) => ({
        jobResult,
        benchmarkRecord,
        selectedBrand,
    })
);

export const getRetailMatchReportDataReady = createSelector(
    [getRetailMatchResult, getRetailBenchmarkMatchResult],
    (jobResult, benchmarkRecord): boolean =>
        jobResult !== null
    && jobResult.chartData !== null
    && jobResult.job !== null
    && benchmarkRecord !== null
);

export const retailMatchLongAddressSelector = createSelector(
    [retailMatchReportDataSelector],
    ({ jobResult, benchmarkRecord }): string => {
        const benchmarkAddressOverride: IStoreScoreRecord
      = getBenchmarkAddressOverride(jobResult, benchmarkRecord);
        return formatLongAddressFromRecord(benchmarkAddressOverride);
    }
);

export const rmMosaicSegmentsSelector = createSelector(
    (state: IRootReducer) => state.matchReportOutput.mosaicSegments,
    (segments: ISegment[]) => segments
);

export const loadingRetailMatchReportSelector = createSelector(
    (state: IRootReducer) => state.matchReportOutput.loadingReport,
    (loadingReport: boolean) => loadingReport
);
